/**
 * Thin fetch wrapper for the platform super-admin SPA.
 *
 * URL strategy: the SPA is served at /admin/... (Spring's AdminUiResourceConfig
 * → classpath:/static/admin/). API calls go to /admin-api/... directly
 * — no tenant prefix to extract because the entire surface is platform-scoped.
 * For cross-tenant operations on /admin-api/leads, the tenant slug rides
 * as a query parameter.
 *
 * `credentials: "include"` flows the SESSION cookie (HttpOnly, set by
 * POST /admin-api/auth/login).
 */

import { useEffect, useState } from "react";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API error ${status}`);
    this.status = status;
    this.body = body;
  }
}

function readCsrfCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

const STATE_CHANGING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";

  if (STATE_CHANGING_METHODS.has(method)) {
    const token = readCsrfCookie();
    if (token) headers["X-XSRF-TOKEN"] = token;
  }

  const normalized = path.startsWith("/") ? path : `/${path}`;
  const res = await fetch(normalized, {
    method,
    credentials: "include",
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let parsed: unknown = text;
  if (text.length > 0 && res.headers.get("content-type")?.includes("application/json")) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // Fall through to raw text.
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, parsed);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

/* ──────────────────────────────────────────────────────────────────────
 * Brand (resolved at runtime from /api/public/bootstrap)
 *
 * The admin SPA is a single brand-agnostic bundle — one
 * classpath:/static/admin/ dir served by AdminUiResourceConfig with no
 * brand split, and the deploy scripts don't rebuild it per brand. So unlike
 * the operator SPA (which bakes the brand in at build time via @brand) it
 * reads the running deployment's brand at runtime from the public bootstrap
 * endpoint, which reflects LEADENGINE_BRAND on the box. Always matches the
 * box it runs on; never stale.
 * ────────────────────────────────────────────────────────────────────── */

export interface PublicBootstrap {
  brand: string;
  defaultVertical: string;
}

const BRAND_DISPLAY_NAMES: Record<string, string> = {
  heidrun: "Heidrun",
  hejson: "Hejson",
};

/** Map a lowercase brand key to its product display name. */
export function brandDisplayName(key: string | null | undefined): string {
  if (!key) return "";
  const k = key.trim().toLowerCase();
  return BRAND_DISPLAY_NAMES[k] ?? k.charAt(0).toUpperCase() + k.slice(1);
}

// Module-level cache: the brand is invariant for the page's lifetime, so a
// single in-flight promise is shared across every caller (login gate, shell
// header) — no duplicate fetches. credentials are omitted: the endpoint is
// permitAll and the gate renders pre-auth.
let bootstrapPromise: Promise<PublicBootstrap> | null = null;

export function fetchPublicBootstrap(): Promise<PublicBootstrap> {
  if (!bootstrapPromise) {
    bootstrapPromise = fetch("/api/public/bootstrap", { credentials: "omit" }).then((res) => {
      if (!res.ok) throw new Error(`bootstrap ${res.status}`);
      return res.json() as Promise<PublicBootstrap>;
    });
  }
  return bootstrapPromise;
}

/**
 * Resolve the running deployment's brand display name and reflect it in the
 * document title (e.g. "Hejson · admin"). Returns "" until the bootstrap
 * call resolves so callers render a neutral fallback rather than flash the
 * wrong brand or the internal "lead-engine" codename.
 */
export function useBrandName(): string {
  const [name, setName] = useState("");
  useEffect(() => {
    let alive = true;
    fetchPublicBootstrap()
      .then((b) => {
        if (!alive) return;
        const display = brandDisplayName(b.brand);
        setName(display);
        if (display) document.title = `${display} · admin`;
      })
      .catch(() => {
        /* keep the neutral fallback */
      });
    return () => {
      alive = false;
    };
  }, []);
  return name;
}

/* ──────────────────────────────────────────────────────────────────────
 * Auth
 * ────────────────────────────────────────────────────────────────────── */

export interface SuperAdminUser {
  id: number;
  email: string;
  full_name: string | null;
  role: string;
}

export async function login(email: string, password: string): Promise<SuperAdminUser> {
  return api.post<SuperAdminUser>("/admin-api/auth/login", { email, password });
}

/**
 * Phase 23 unified login response. The backend at POST /api/public/login
 * tries super_admin first (master.super_admin / BCrypt), then iterates
 * active tenants (each tenant's app_user / Argon2). Returns a `redirect`
 * field the SPA hard-navigates to so the user lands on the right bundle:
 *   - "/admin/"             — super-admin
 *   - "/t/<slug>/ui/"       — tenant operator
 * Sole error case is 401 invalid_credentials.
 */
export interface UnifiedLoginResponse {
  redirect: string;
  principal: "super_admin" | "app_user";
  tenant?: string;
  email: string;
  full_name?: string | null;
  role: string;
}

export async function loginUnified(email: string, password: string): Promise<UnifiedLoginResponse> {
  return api.post<UnifiedLoginResponse>("/api/public/login", { email, password });
}

/**
 * Phase 23 forgot-password (request). Backend scans active tenants for
 * the email, mints a reset token, and sends the email if found. Always
 * returns 200 so the SPA can't probe whether the address is registered.
 * The reset link in the email points at the operator SPA's
 * /t/<slug>/ui/password-reset/<token> page.
 */
export async function requestPasswordResetUnified(email: string): Promise<void> {
  await api.post<unknown>("/api/public/password-reset", { email });
}

export async function logout(): Promise<void> {
  await api.post<unknown>("/admin-api/auth/logout");
}

export async function fetchCurrentUser(): Promise<SuperAdminUser | null> {
  try {
    return await api.get<SuperAdminUser>("/admin-api/auth/me");
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

/* ──────────────────────────────────────────────────────────────────────
 * Self-service account (Phase 47) — the super-admin's own "user home",
 * reached from the email chip in the shell. Email + password changes
 * confirm the current password server-side; every change is audit-logged.
 * These live under /admin-api/account/* (CSRF-protected + SUPER_ADMIN-gated),
 * NOT the anonymous /admin-api/auth/** bootstrap surface.
 * ────────────────────────────────────────────────────────────────────── */

export interface AdminAccount {
  id: number;
  email: string;
  fullName: string | null;
  role: string;
  hasPassword: boolean;
  lastLoginAt: string | null;
  createdAt: string | null;
}

export async function getMyAccount(): Promise<AdminAccount> {
  return api.get<AdminAccount>("/admin-api/account");
}

/** Change the super-admin's display name (blank clears it). */
export async function updateMyAdminProfile(fullName: string): Promise<AdminAccount> {
  return api.patch<AdminAccount>("/admin-api/account/profile", { fullName });
}

/**
 * Change the super-admin's login email. Requires the current password
 * (403 `invalid_password`); 409 `email_taken` when another admin uses it.
 */
export async function changeMyAdminEmail(
  currentPassword: string,
  newEmail: string,
): Promise<AdminAccount> {
  return api.post<AdminAccount>("/admin-api/account/email", { currentPassword, newEmail });
}

/**
 * Change the super-admin's password. Requires the current password
 * (403 `invalid_password`); other sessions are revoked, the current one stays.
 */
export async function changeMyAdminPassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ status: string }> {
  return api.post<{ status: string }>("/admin-api/account/password", {
    currentPassword,
    newPassword,
  });
}

/* ──────────────────────────────────────────────────────────────────────
 * Tenants
 * ────────────────────────────────────────────────────────────────────── */

export interface TenantSummary {
  slug: string;
  name: string;
  distributor: string | null;
  /** Primary vertical (== `verticals[0]`). Kept as a top-level field for engine-side reads + back-compat. */
  vertical: string | null;
  /** All verticals the tenant is configured for, ordered with primary first. Pre-Phase-18 rows may be null. */
  verticals: string[] | null;
  status: string | null;
  dbName: string | null;
  s3Bucket: string | null;
  timezone: string | null;
  locale: string | null;
  /**
   * Customer-org branding (Phase 47) — the "tenant brand_name" interpolated
   * into outbound copy (`$brand`), NOT the product brand. Editable post-
   * creation from the tenant detail page. Null = unset.
   */
  brandName: string | null;
  /** Email sender display identity. Null = unset. */
  fromEmail: string | null;
  /** SMS sender id. Null = unset. */
  senderIdSms: string | null;
  /**
   * Per-tenant AI-identity disclosure (Swedish). Null = use the shared
   * `_shared/disclosure_ai.sv.vm` template at render time. Edited from the
   * tenant detail page in the admin SPA.
   */
  aiDisclosureSv: string | null;
  /** English counterpart to {@link aiDisclosureSv}. */
  aiDisclosureEn: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CreateTenantBody {
  slug: string;
  name: string;
  distributor: string;
  /**
   * Single-vertical legacy field. New Slice C callers populate `verticals`
   * instead; the backend back-compat path still accepts `vertical` alone.
   * When both are provided, the backend forces `verticals[0] === vertical`.
   */
  vertical?: string;
  /** Required for new callers — at least one vertical. First entry is the primary. */
  verticals?: string[];
  brandName?: string | null;
  primaryColor?: string | null;
  logoUrl?: string | null;
  fromEmail?: string | null;
  fromName?: string | null;
  senderIdSms?: string | null;
  webhookSecret?: string | null;
  locale?: string | null;
  subscriptionTier?: string | null;
}

export async function listTenants(): Promise<TenantSummary[]> {
  return api.get<TenantSummary[]>("/admin-api/tenants");
}

export async function getTenant(slug: string): Promise<TenantSummary> {
  return api.get<TenantSummary>(`/admin-api/tenants/${encodeURIComponent(slug)}`);
}

export async function createTenant(body: CreateTenantBody): Promise<TenantSummary> {
  return api.post<TenantSummary>("/admin-api/tenants", body);
}

/* ──────────────────────────────────────────────────────────────────────
 * Tenant app-users (Phase 35 Slice C) — super-admin provisions/lists the
 * tenant's operator logins from the /admin console.
 * ────────────────────────────────────────────────────────────────────── */

export type TenantAppUser = {
  id: string;
  email: string;
  fullName: string | null;
  role: string;
  enabled: boolean;
  hasPassword: boolean;
  createdAt: string | null;
  lastLoginAt: string | null;
};

export async function listTenantAppUsers(
  slug: string,
): Promise<{ tenant: string; count: number; users: TenantAppUser[] }> {
  return api.get(`/admin-api/tenants/${encodeURIComponent(slug)}/app-users`);
}

export async function inviteTenantAppUser(
  slug: string,
  body: { email: string; fullName: string | null; role: string },
): Promise<TenantAppUser & { inviteSent: boolean; setPasswordUrl: string }> {
  return api.post(`/admin-api/tenants/${encodeURIComponent(slug)}/app-users`, body);
}

/**
 * Phase 36 — re-issue a pending tenant user's set-password invite (fresh token,
 * stale link dies). Only valid while the user hasn't set a password.
 */
export async function resendTenantAppUserInvite(
  slug: string,
  id: string,
): Promise<{ id: string; email: string; role: string; inviteSent: boolean; setPasswordUrl: string }> {
  return api.post(
    `/admin-api/tenants/${encodeURIComponent(slug)}/app-users/${encodeURIComponent(id)}/resend-invite`,
  );
}

/**
 * Hard-remove a tenant operator login (tenant_admin / operator) + its tokens.
 * sales_rep is refused (409 sales_rep_managed_by_tenant) — it's provisioned and
 * retired from the tenant's own Users page.
 */
export async function removeTenantAppUser(
  slug: string,
  id: string,
): Promise<{ id: string; email: string; role: string; removed: boolean }> {
  return api.del(
    `/admin-api/tenants/${encodeURIComponent(slug)}/app-users/${encodeURIComponent(id)}`,
  );
}

/**
 * Impersonate a tenant user ("sign in as"). Swaps THIS browser session to a
 * real tenant operator session for the target user and returns the operator
 * SPA URL to navigate to. The original super-admin is stashed server-side and
 * restored by the operator SPA's "Exit" (POST /api/auth/impersonation/stop).
 * Disabled users are refused (409 user_disabled).
 */
export async function impersonateTenantAppUser(
  slug: string,
  id: string,
): Promise<{ redirect: string; tenant: string; targetEmail: string; targetRole: string }> {
  return api.post(
    `/admin-api/tenants/${encodeURIComponent(slug)}/app-users/${encodeURIComponent(id)}/impersonate`,
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * SMS numbers (Phase 42) — platform-owned from-number pool + per-tenant
 * allocation. Replaces the operator BYO-credentials surface: the super-admin
 * loads numbers into the pool and allocates one to each tenant.
 * ────────────────────────────────────────────────────────────────────── */

export type SmsNumberStatus = "FREE" | "ALLOCATED" | "RETIRED";

export interface SmsNumber {
  id: number;
  provider: string; // "46elks" | "twilio"
  e164: string;
  label: string | null;
  status: SmsNumberStatus;
  tenantSlug: string | null;
  createdAt: string | null;
  allocatedAt: string | null;
  retiredAt: string | null;
}

export interface TenantSmsState {
  tenantSlug: string;
  /** Active provider for outbound SMS, or null when no number is allocated. */
  primary: string | null;
  /** Active from-number (E.164), or null. */
  fromNumber: string | null;
  /** Explicit operating country (ISO-2), or null when auto-detected (Phase 53). */
  country: string | null;
  /** Country the router actually resolves (explicit → sender → locale → SE). */
  resolvedCountry: string | null;
  /** Carrier the router will use: pinned primary, else country-derived. */
  carrier: string | null;
  /** Pool rows currently allocated to this tenant (0 or 1 under one-active-per-tenant). */
  numbers: {
    id: number;
    provider: string;
    e164: string;
    label: string | null;
    status: string;
    allocatedAt: string | null;
  }[];
}

export async function listSmsNumbers(): Promise<{ count: number; numbers: SmsNumber[] }> {
  return api.get("/admin-api/sms-numbers");
}

export async function addSmsNumber(body: {
  provider: string;
  e164: string;
  label?: string | null;
}): Promise<SmsNumber> {
  return api.post("/admin-api/sms-numbers", body);
}

export async function retireSmsNumber(id: number): Promise<{ status: string; id: number }> {
  return api.post(`/admin-api/sms-numbers/${id}/retire`);
}

export async function deleteSmsNumber(id: number): Promise<{ status: string; id: number }> {
  return api.del(`/admin-api/sms-numbers/${id}`);
}

export async function getTenantSmsNumber(slug: string): Promise<TenantSmsState> {
  return api.get(`/admin-api/tenants/${encodeURIComponent(slug)}/sms-number`);
}

export async function allocateTenantSmsNumber(
  slug: string,
  numberId: number,
): Promise<TenantSmsState> {
  return api.post(`/admin-api/tenants/${encodeURIComponent(slug)}/sms-number/allocate`, {
    numberId,
  });
}

export async function assignTenantSmsNumber(
  slug: string,
  body: { provider: string; e164: string; label?: string | null },
): Promise<TenantSmsState> {
  return api.post(`/admin-api/tenants/${encodeURIComponent(slug)}/sms-number/assign`, body);
}

export async function releaseTenantSmsNumber(slug: string): Promise<TenantSmsState> {
  return api.del(`/admin-api/tenants/${encodeURIComponent(slug)}/sms-number`);
}

/**
 * Set the tenant's operating country (Phase 53) — drives SMS carrier selection
 * (Nordics → 46elks, else → Twilio) and phone normalisation at intake. Pass an
 * empty string to clear the override and fall back to auto-detection.
 */
export async function setTenantSmsCountry(
  slug: string,
  country: string,
): Promise<TenantSmsState> {
  return api.put(`/admin-api/tenants/${encodeURIComponent(slug)}/sms-number/country`, {
    country,
  });
}

/**
 * Rent a brand-new number from 46elks and allocate it to the tenant in one
 * step (the number's sms_url is set to the tenant's inbound webhook at
 * allocation time). Recurring charge on the platform 46elks account.
 */
export async function rentTenantSmsNumber(
  slug: string,
  body?: { provider?: string; country?: string; capabilities?: string },
): Promise<TenantSmsState> {
  return api.post(`/admin-api/tenants/${encodeURIComponent(slug)}/sms-number/rent`, body ?? {});
}

/* ──────────────────────────────────────────────────────────────────────
 * Super-admins (Phase 35 Slice D) — platform operators invite peers + set
 * their own password via an emailed link.
 * ────────────────────────────────────────────────────────────────────── */

export type SuperAdminRow = {
  id: number;
  email: string;
  fullName: string | null;
  enabled: boolean;
  hasPassword: boolean;
  lastLoginAt: string | null;
  createdAt: string | null;
};

export async function listSuperAdmins(): Promise<{ count: number; superAdmins: SuperAdminRow[] }> {
  return api.get("/admin-api/super-admins");
}

export async function inviteSuperAdmin(
  body: { email: string; fullName: string | null },
): Promise<SuperAdminRow & { inviteSent: boolean; setPasswordUrl: string }> {
  return api.post("/admin-api/super-admins", body);
}

/**
 * Phase 36 — re-issue a pending super-admin's set-password invite (fresh token,
 * stale link dies). Only valid while the admin hasn't set a password.
 */
export async function resendSuperAdminInvite(
  id: number,
): Promise<SuperAdminRow & { inviteSent: boolean; setPasswordUrl: string }> {
  return api.post(`/admin-api/super-admins/${encodeURIComponent(String(id))}/resend-invite`);
}

/**
 * Phase 37 — block / unblock / remove. Disable is the reversible "can't get in"
 * switch (enabled=0, login refused); enable restores it; delete is a hard remove
 * (auth tokens cascade). The backend rejects acting on yourself
 * (`cannot_modify_self`) or the last enabled admin (`last_admin`) with 409.
 */
export async function disableSuperAdmin(id: number): Promise<SuperAdminRow> {
  return api.post(`/admin-api/super-admins/${encodeURIComponent(String(id))}/disable`);
}

export async function enableSuperAdmin(id: number): Promise<SuperAdminRow> {
  return api.post(`/admin-api/super-admins/${encodeURIComponent(String(id))}/enable`);
}

export async function deleteSuperAdmin(id: number): Promise<{ deleted: boolean; id: number }> {
  return api.del(`/admin-api/super-admins/${encodeURIComponent(String(id))}`);
}

/** Consume a super-admin set-password / reset token (anonymous endpoint). */
export async function confirmAdminPasswordReset(token: string, newPassword: string): Promise<void> {
  await api.post(`/admin-api/auth/password-reset/${encodeURIComponent(token)}`, { newPassword });
}

/**
 * Persist the per-tenant AI-identity disclosure override. Pass `null` or
 * empty string in either field to clear that locale and fall back to the
 * shared `_shared/disclosure_ai.{locale}.vm` template at render time.
 * Returns the refreshed tenant summary so the SPA can render the persisted
 * state without a follow-up GET.
 */
export async function putTenantAiDisclosure(
  slug: string,
  sv: string | null,
  en: string | null,
): Promise<TenantSummary> {
  return api.put<TenantSummary>(
    `/admin-api/tenants/${encodeURIComponent(slug)}/ai-disclosure`,
    { sv, en },
  );
}

/**
 * Phase 47 — persist the customer-org branding (brand_name / email sender /
 * SMS sender id) for an existing tenant. Blank strings clear the column.
 * Returns the refreshed summary so the SPA re-renders without a follow-up GET.
 */
export async function putTenantBranding(
  slug: string,
  body: { brandName: string | null; fromEmail: string | null; senderIdSms: string | null },
): Promise<TenantSummary> {
  return api.put<TenantSummary>(
    `/admin-api/tenants/${encodeURIComponent(slug)}/branding`,
    body,
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Verticals catalog (Phase 18 / Slice A)
 * ────────────────────────────────────────────────────────────────────── */

export interface AvailableVertical {
  key: string;
  hasPack: boolean;
}

/**
 * Lists vertical keys discovered at boot by the backend's `VerticalLoader`,
 * minus the reserved `_shared` pack. The super-admin SPA uses this to
 * populate the create-tenant vertical picker (replacing the hardcoded
 * `["fitness", "solar", "roofing", "real_estate"]` array, which was lying
 * about what packs actually ship on disk).
 */
export async function listAvailableVerticals(): Promise<AvailableVertical[]> {
  const response = await api.get<{ verticals: AvailableVertical[] }>("/admin-api/verticals");
  return response.verticals;
}

/* ──────────────────────────────────────────────────────────────────────
 * Cross-tenant leads (admin recovery)
 * ────────────────────────────────────────────────────────────────────── */

export interface AdminLeadSummary {
  id: string;
  source: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
  status: string | null;
  engineState: string | null;
  engineEnabled: boolean;
  receivedAt: string | null;
  lastActivityAt: string | null;
  claimedAt: string | null;
  claimedBy: string | null;
  engineNextActionAt: string | null;
}

export interface ListAdminLeadsResponse {
  tenant: string;
  limit: number;
  count: number;
  leads: AdminLeadSummary[];
}

export interface AdminLeadFilters {
  tenant: string;
  engineState?: string;
  claimStatus?: "claimed" | "unclaimed" | "stuck";
  since?: string;
  limit?: number;
}

export async function listAdminLeads(filters: AdminLeadFilters): Promise<ListAdminLeadsResponse> {
  const params = new URLSearchParams();
  params.set("tenant", filters.tenant);
  if (filters.engineState) params.set("engine_state", filters.engineState);
  if (filters.claimStatus) params.set("claim_status", filters.claimStatus);
  if (filters.since) params.set("since", filters.since);
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  return api.get(`/admin-api/leads?${params.toString()}`);
}

export async function getAdminLead(tenant: string, id: string): Promise<Record<string, unknown>> {
  return api.get(`/admin-api/leads/${encodeURIComponent(id)}?tenant=${encodeURIComponent(tenant)}`);
}

function leadActionQs(tenant: string, actor?: string, reason?: string): string {
  const params = new URLSearchParams();
  params.set("tenant", tenant);
  if (actor) params.set("actor", actor);
  if (reason) params.set("reason", reason);
  return params.toString();
}

export async function rearmLead(tenant: string, id: string, actor?: string): Promise<Record<string, unknown>> {
  return api.post(`/admin-api/leads/${encodeURIComponent(id)}/rearm?${leadActionQs(tenant, actor)}`);
}

export async function clearLeadClaim(tenant: string, id: string, actor?: string): Promise<Record<string, unknown>> {
  return api.post(`/admin-api/leads/${encodeURIComponent(id)}/clear-claim?${leadActionQs(tenant, actor)}`);
}

export async function escalateAdminLead(tenant: string, id: string, actor?: string, reason?: string): Promise<Record<string, unknown>> {
  return api.post(`/admin-api/leads/${encodeURIComponent(id)}/escalate?${leadActionQs(tenant, actor, reason)}`);
}

export async function disableLeadEngine(tenant: string, id: string, actor?: string, reason?: string): Promise<Record<string, unknown>> {
  return api.post(`/admin-api/leads/${encodeURIComponent(id)}/disable-engine?${leadActionQs(tenant, actor, reason)}`);
}

/* ──────────────────────────────────────────────────────────────────────
 * Dispatcher
 * ────────────────────────────────────────────────────────────────────── */

export interface DispatcherTenantStats {
  tenant: string;
  dueCount: number | null;
  stuckClaim: number | null;
  retryHigh: number | null;
  engineState: Record<string, number> | null;
  lastTickAt: string | null;
  lastTickDurationMs: number | null;
}

export interface DispatcherStats {
  workerId: string | null;
  tenants: DispatcherTenantStats[];
}

export async function getDispatcherStats(): Promise<DispatcherStats> {
  return api.get<DispatcherStats>("/admin-api/dispatcher/stats");
}

export async function tickDispatcher(tenant: string, actor?: string): Promise<{
  tenant: string;
  processed: number;
  durationMs: number;
  tickedAt: string;
}> {
  const params = new URLSearchParams();
  params.set("tenant", tenant);
  if (actor) params.set("actor", actor);
  return api.post(`/admin-api/dispatcher/tick?${params.toString()}`);
}

/* ──────────────────────────────────────────────────────────────────────
 * Audit log
 * ────────────────────────────────────────────────────────────────────── */

export interface AuditLogRow {
  id: number;
  actor: string | null;
  action: string;
  tenant: string | null;
  targetType: string | null;
  targetId: string | null;
  /**
   * `details` is the `audit_log.details` JSON column server-parsed back
   * into JS — so it can be an object, an array, or null. Old rows
   * occasionally come back as a plain string. Render with
   * `typeof === "string" ? d : JSON.stringify(d)` to avoid React #31.
   */
  details: string | Record<string, unknown> | unknown[] | null;
  createdAt: string;
}

export interface AuditLogFilters {
  tenant?: string;
  actor?: string;
  action?: string;
  since?: string;
  limit?: number;
  offset?: number;
}

export interface AuditLogResponse {
  limit: number;
  offset: number;
  count: number;
  rows: AuditLogRow[];
}

export async function getAuditLog(filters: AuditLogFilters = {}): Promise<AuditLogResponse> {
  const params = new URLSearchParams();
  if (filters.tenant) params.set("tenant", filters.tenant);
  if (filters.actor) params.set("actor", filters.actor);
  if (filters.action) params.set("action", filters.action);
  if (filters.since) params.set("since", filters.since);
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.offset !== undefined) params.set("offset", String(filters.offset));
  const qs = params.toString();
  return api.get<AuditLogResponse>(`/admin-api/audit-log${qs ? `?${qs}` : ""}`);
}

/* ──────────────────────────────────────────────────────────────────────
 * Tenant health
 * ────────────────────────────────────────────────────────────────────── */

export interface TenantHealthRow {
  slug: string;
  status: string;
  dueCount: number | null;
  pool: {
    name: string | null;
    total: number | null;
    active: number | null;
    idle: number | null;
    waiting: number | null;
  } | null;
  lastTickAt: string | null;
  lastTickDurationMs: number | null;
}

export interface TenantHealthResponse {
  workerId: string | null;
  tenants: TenantHealthRow[];
}

export async function getTenantHealth(): Promise<TenantHealthResponse> {
  return api.get<TenantHealthResponse>("/admin-api/health/tenants");
}

/* ──────────────────────────────────────────────────────────────────────
 * Google OAuth jump-point
 * ────────────────────────────────────────────────────────────────────── */

export function googleAuthorizeUrl(slug: string, salesUserId: string, returnTo?: string): string {
  const params = new URLSearchParams();
  params.set("slug", slug);
  params.set("sales_user_id", salesUserId);
  if (returnTo) params.set("return_to", returnTo);
  return `/admin-api/integrations/google/authorize?${params.toString()}`;
}

/* ──────────────────────────────────────────────────────────────────────
 * Phase 13 — prompt modules catalog
 * ────────────────────────────────────────────────────────────────────── */

export type PromptModuleStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";

export interface PromptModule {
  id: string;
  vertical: string;
  name: string;
  description: string | null;
  status: PromptModuleStatus;
  /** Phase 34 — true if this is the platform default module for its vertical. */
  isDefault?: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy: string | null;
  /**
   * Phase 17 — number of distinct tenants that have this module on
   * their {@code tenant_prompt_module_allowlist}. Tenants with an
   * empty allowlist (unrestricted) are NOT counted here — they see
   * every module by default but no explicit row exists for the count.
   */
  allowlistTenantCount?: number;
}

export interface PromptModuleTemplate {
  moduleId: string;
  templateName: string;
  locale: string;
  body: string;
  updatedAt: string | null;
}

export async function listPromptModules(vertical?: string): Promise<{ count: number; modules: PromptModule[] }> {
  const qs = vertical ? `?vertical=${encodeURIComponent(vertical)}` : "";
  return api.get(`/admin-api/vertical-prompt-modules${qs}`);
}

export async function createPromptModule(body: {
  vertical: string;
  name: string;
  description?: string | null;
  status?: PromptModuleStatus;
}): Promise<PromptModule> {
  return api.post("/admin-api/vertical-prompt-modules", body);
}

export async function getPromptModule(moduleId: string): Promise<PromptModule> {
  return api.get(`/admin-api/vertical-prompt-modules/${encodeURIComponent(moduleId)}`);
}

export async function updatePromptModule(moduleId: string, body: {
  name?: string;
  description?: string | null;
  status?: PromptModuleStatus;
}): Promise<PromptModule> {
  return api.put(`/admin-api/vertical-prompt-modules/${encodeURIComponent(moduleId)}`, body);
}

/** Phase 34 — mark a module as the platform default for its vertical (clears the sibling default). */
export async function setPromptModuleDefault(moduleId: string): Promise<PromptModule> {
  return api.put(`/admin-api/vertical-prompt-modules/${encodeURIComponent(moduleId)}/default`, {});
}

export async function deletePromptModule(moduleId: string): Promise<void> {
  await api.del(`/admin-api/vertical-prompt-modules/${encodeURIComponent(moduleId)}`);
}

export async function listPromptModuleTemplates(moduleId: string): Promise<{
  count: number;
  templates: PromptModuleTemplate[];
}> {
  return api.get(`/admin-api/vertical-prompt-modules/${encodeURIComponent(moduleId)}/templates`);
}

export async function getPromptModuleTemplate(
  moduleId: string,
  templateName: string,
  locale: string,
): Promise<PromptModuleTemplate | null> {
  try {
    return await api.get<PromptModuleTemplate>(
      `/admin-api/vertical-prompt-modules/${encodeURIComponent(moduleId)}/templates/${encodeURIComponent(templateName)}/${encodeURIComponent(locale)}`,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function putPromptModuleTemplate(
  moduleId: string,
  templateName: string,
  locale: string,
  body: string,
): Promise<PromptModuleTemplate> {
  return api.put(
    `/admin-api/vertical-prompt-modules/${encodeURIComponent(moduleId)}/templates/${encodeURIComponent(templateName)}/${encodeURIComponent(locale)}`,
    { body },
  );
}

export async function deletePromptModuleTemplate(
  moduleId: string,
  templateName: string,
  locale: string,
): Promise<void> {
  await api.del(
    `/admin-api/vertical-prompt-modules/${encodeURIComponent(moduleId)}/templates/${encodeURIComponent(templateName)}/${encodeURIComponent(locale)}`,
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Phase 13 — facts schema
 * ────────────────────────────────────────────────────────────────────── */

export type FactsFieldType = "TEXT" | "TEXTAREA" | "SELECT" | "BOOLEAN";

export interface FactsField {
  key: string;
  labelSv: string;
  labelEn: string;
  type: FactsFieldType;
  required: boolean;
  order: number;
  placeholder: string | null;
  selectOptions: string | null;
}

export async function getFactsSchemaAdmin(vertical: string): Promise<{ vertical: string; fields: FactsField[] }> {
  return api.get(`/admin-api/verticals/${encodeURIComponent(vertical)}/facts-schema`);
}

export async function putFactsSchema(vertical: string, fields: FactsField[]): Promise<{ vertical: string; fields: FactsField[] }> {
  return api.put(`/admin-api/verticals/${encodeURIComponent(vertical)}/facts-schema`, { fields });
}

/* ──────────────────────────────────────────────────────────────────────
 * Phase 13 — tenant prompt-module assignment
 * ────────────────────────────────────────────────────────────────────── */

export interface TenantPromptModuleAssignment {
  tenant: string;
  vertical: string;
  promptModuleId: string | null;
  moduleName?: string;
  moduleStatus?: PromptModuleStatus;
}

export async function getTenantPromptModule(slug: string): Promise<TenantPromptModuleAssignment> {
  return api.get(`/admin-api/tenants/${encodeURIComponent(slug)}/prompt-module`);
}

export async function putTenantPromptModule(slug: string, moduleId: string | null): Promise<TenantPromptModuleAssignment> {
  return api.put(`/admin-api/tenants/${encodeURIComponent(slug)}/prompt-module`, { moduleId });
}

/* ──────────────────────────────────────────────────────────────────────
 * Phase 17 — per-tenant prompt-module allowlist (super-admin gate)
 * ────────────────────────────────────────────────────────────────────── */

export interface TenantPromptModuleAllowlist {
  tenant: string;
  vertical: string;
  /** true iff ≥1 row exists in tenant_prompt_module_allowlist for this slug. */
  allowlistActive: boolean;
  count: number;
  moduleIds: string[];
}

export async function getTenantPromptModuleAllowlist(
  slug: string,
): Promise<TenantPromptModuleAllowlist> {
  return api.get(`/admin-api/tenants/${encodeURIComponent(slug)}/prompt-module-allowlist`);
}

export async function putTenantPromptModuleAllowlist(
  slug: string,
  moduleIds: string[],
): Promise<TenantPromptModuleAllowlist> {
  return api.put(
    `/admin-api/tenants/${encodeURIComponent(slug)}/prompt-module-allowlist`,
    { moduleIds },
  );
}
